import { useEffect, useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/lib/pwa";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { MultiSelect } from "@/components/sales/MultiSelect";
import { useAuth } from "@/context/AuthContext";
import { useSource } from "@/context/SourceContext";
import api from "@/lib/api";
import { CATEGORY_LABEL, CATEGORY_OPTIONS, dayLabel } from "@/lib/filters";
import { QUICK_RANGES } from "@/lib/ranges";
import { cn } from "@/lib/utils";

const CHIP_LABELS = { category: "Category", customer: "Customer", salesman: "Salesperson", account: "Account", fs_group: "FS group", company: "Company" };

export const useCompanies = (enabled) => {
  const [list, setList] = useState([]);
  useEffect(() => { if (enabled) api.get("/admin/companies").then((r) => setList(r.data.map((c) => ({ value: c.id, label: c.name })))).catch(() => setList([])); }, [enabled]);
  return list;
};

export const Chip = ({ label, onRemove, testid }) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2.5 py-0.5 text-xs font-medium text-teal-900" data-testid={testid}>
    {label}
    {onRemove && <button type="button" onClick={onRemove} aria-label={`Remove ${label}`} className="rounded-full p-0.5 hover:bg-teal-100" data-testid={`${testid}-remove`}><X className="h-3 w-3" /></button>}
  </span>
);

const Wrap = ({ mobile, open, setOpen, children }) => (mobile ? (
  <Sheet open={open} onOpenChange={setOpen}>
    <SheetContent side="bottom" className="pb-safe max-h-[88dvh] space-y-4 overflow-y-auto rounded-t-2xl" data-testid="filter-sheet">
      <SheetTitle>Filters</SheetTitle>
      {children}
      <Button type="button" onClick={() => setOpen(false)} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="filter-sheet-done">Show results</Button>
    </SheetContent>
  </Sheet>
) : <>{children}</>);

export const FilterBar = ({ fl, periodLabel, allHistory }) => {
  const src = useSource();
  const { user } = useAuth() || {};
  const superAdmin = !src.readOnly && user?.is_super_admin;
  const companies = useCompanies(superAdmin);
  const { f, update } = fl;
  const mobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const companyName = (id) => companies.find((c) => c.value === id)?.label || id;
  const [min, max] = src.bounds || [];
  const inside = (r) => !min || (r[0] >= min && r[1] <= max);
  const presets = [...QUICK_RANGES.map((q) => [q.key, q.label, q.range()]), ...(allHistory ? [["all_history", "All history", allHistory]] : []), ...(min ? [["link_range", "Whole permitted range", [min, max]]] : [])].filter(([, , r]) => inside(r));
  const catOptions = CATEGORY_OPTIONS.filter((o) => !(src.excluded || []).includes(o.value));
  const chips = [
    ...(fl.dated ? [{ key: "date", label: `Date: ${dayLabel(f.from)} – ${dayLabel(f.to)}`, remove: () => update({ from: null, to: null }) }] : []),
    ...Object.keys(CHIP_LABELS).flatMap((k) => f[k].map((v) => ({
      key: `${k}-${v}`, label: `${CHIP_LABELS[k]}: ${k === "category" ? CATEGORY_LABEL[v] || v : k === "company" ? companyName(v) : v}`,
      remove: () => update({ [k]: f[k].filter((x) => x !== v) }),
    }))),
  ];

  return (
    <div className="rise-in space-y-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm sm:p-4" data-testid="filter-bar">
      {mobile && <Button type="button" variant="outline" onClick={() => setOpen(true)} className="w-full gap-2" data-testid="filter-open-sheet"><SlidersHorizontal className="h-4 w-4" /> Filters{chips.length ? ` (${chips.length})` : ""}</Button>}
      <Wrap mobile={mobile} open={open} setOpen={setOpen}>
      <div className="flex flex-wrap items-center gap-2">
        {presets.map(([k, l, r]) => (
          <button key={k} type="button" onClick={() => update({ from: r[0], to: r[1] })} data-testid={`filter-range-${k}`}
            className={cn("rounded-full border px-3 py-1 text-xs font-medium transition-colors", fl.dated && f.from === r[0] && f.to === r[1] ? "border-teal-600 bg-teal-50 text-teal-800" : "border-slate-200 text-slate-600 hover:border-slate-400")}>{l}</button>
        ))}
        <DateInput min={min} max={max} value={fl.range?.[0] || ""} onChange={(x) => x && update({ from: x, to: fl.range?.[1] || x })} className="h-8 w-40" testid="filter-from-input" />
        <DateInput min={min} max={max} value={fl.range?.[1] || ""} onChange={(x) => x && update({ from: fl.range?.[0] || x, to: x })} className="h-8 w-40" testid="filter-to-input" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <MultiSelect label="Category" staticOptions={catOptions} value={f.category} onChange={(v) => update({ category: v })} testid="filter-category" chips={false} />
        <MultiSelect field="customer" endpoint="/filters/options" label="Customer" value={f.customer} onChange={(v) => update({ customer: v })} testid="filter-customer" chips={false} extraQuery={fl.f.company} />
        <MultiSelect field="salesman" endpoint="/filters/options" label="Salesperson" value={f.salesman} onChange={(v) => update({ salesman: v })} testid="filter-salesman" chips={false} extraQuery={fl.f.company} />
        <MultiSelect field="account" endpoint="/filters/options" label="Account" value={f.account} onChange={(v) => update({ account: v })} testid="filter-account" chips={false} extraQuery={fl.f.company} />
        <MultiSelect field="fs_group" endpoint="/filters/options" label="FS group" value={f.fs_group} onChange={(v) => update({ fs_group: v })} testid="filter-fs-group" chips={false} extraQuery={fl.f.company} />
        {superAdmin && <MultiSelect label="Company" staticOptions={companies} value={f.company} onChange={(v) => update({ company: v })} testid="filter-company" chips={false} />}
      </div>
      </Wrap>
      <div className={cn("flex items-center gap-2 border-t border-slate-100 pt-3", mobile ? "-mx-1 flex-nowrap overflow-x-auto whitespace-nowrap px-1" : "flex-wrap")} data-testid="filter-chips">
        {chips.length === 0
          ? <span className="text-xs text-slate-500" data-testid="filter-none">No filters applied — showing all data for {periodLabel}</span>
          : <>
            {chips.map((c) => <Chip key={c.key} label={c.label} onRemove={c.remove} testid={`filter-chip-${c.key}`} />)}
            <button type="button" onClick={fl.clear} className="text-xs font-medium text-slate-600 underline-offset-2 hover:underline" data-testid="filter-clear-all">Clear all</button>
          </>}
      </div>
    </div>
  );
};
